// src/components/workAccountability/CompletionDialog.tsx
//
// Part 7: the Work Accountability completion dialog. It builds itself from the
// backend's effective requirements (completion note / variance explanation /
// final time / proof) and submits POST /tasks/:taskId/complete. Backend
// validation is authoritative: a 422 renders the unmet requirements inline with
// the resolver's explanations. Never duplicates validation.

import { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import TaskAttachments from '../tasks/TaskAttachments';
import { useGetEffectivePolicyQuery, useCompleteTaskWaMutation } from '../../api/accountabilityApi';
import type { CompletionUnmetError, UnmetRequirement } from '../../types/workAccountability';
import { SYSTEM_DEFAULT_RULES } from './waFormat';
import { Banner } from './waControls';

export interface CompletionDialogTask {
  id: string;
  workspaceId: string;
  assigneeUserId: string | null;
  estimatedMinutes: number | null;
  projectId?: string | null;
}

interface Props {
  open: boolean;
  task: CompletionDialogTask;
  onClose: () => void;
  onCompleted: () => void;
}

const UNMET_LABEL: Record<UnmetRequirement['requirement'], string> = {
  requiredTime: 'Time must be logged',
  requiredCompletionNote: 'A completion note is required',
  requiredProof: 'Proof of done is required',
  estimateRequired: 'An estimate is required',
  varianceExplanationRequired: 'A variance explanation is required',
};

export default function CompletionDialog({ open, task, onClose, onCompleted }: Props) {
  const { data: effective, isLoading: rulesLoading, isError: rulesError } = useGetEffectivePolicyQuery(
    open ? { userId: task.assigneeUserId ?? undefined, workItemId: task.id, projectId: task.projectId ?? undefined, assigneeUserId: task.assigneeUserId ?? undefined } : undefined,
    { skip: !open },
  );
  const rules = effective?.effective ?? SYSTEM_DEFAULT_RULES;
  const [complete, { isLoading }] = useCompleteTaskWaMutation();

  const [completionNote, setCompletionNote] = useState('');
  const [varianceExplanation, setVarianceExplanation] = useState('');
  const [finalMinutes, setFinalMinutes] = useState('');
  const [unmet, setUnmet] = useState<UnmetRequirement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset all form state each time the dialog opens (prevents cross-task leakage
  // when the dialog stays mounted between opens).
  useEffect(() => {
    if (open) {
      setCompletionNote('');
      setVarianceExplanation('');
      setFinalMinutes('');
      setUnmet(null);
      setError(null);
    }
  }, [open]);

  // If the effective policy could not be resolved, don't silently hide fields —
  // show all of them so the user can satisfy whatever the backend requires (the
  // 422 remains authoritative).
  const show = useMemo(
    () => ({
      note: rulesError || rules.completionNote !== 'disabled',
      variance: rulesError || rules.variance.explanationRequired,
      finalTime: rulesError || rules.requireTimeBeforeCompletion || rules.timeTracking.mode !== 'disabled',
      proof: rulesError || rules.proofOfDone !== 'disabled',
    }),
    [rules, rulesError],
  );

  const submit = async () => {
    setUnmet(null);
    setError(null);
    const fam = finalMinutes.trim() ? Math.floor(Number(finalMinutes)) : null;
    try {
      await complete({
        taskId: task.id,
        completionNote: completionNote.trim() || null,
        varianceExplanation: varianceExplanation.trim() || null,
        finalAdjustmentMinutes: fam && fam > 0 ? fam : null,
      }).unwrap();
      onCompleted();
      onClose();
    } catch (e) {
      const body = (e as { status?: number; data?: CompletionUnmetError }).data;
      if ((e as { status?: number }).status === 422 && body?.code === 'WA_COMPLETION_REQUIREMENTS_UNMET') {
        setUnmet(body.details.unmet);
      } else {
        setError((e as { data?: { error?: string } })?.data?.error ?? 'Could not complete the task.');
      }
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" onClick={onClose} disabled={isLoading}>Cancel</Button>
      <Button variant="primary" onClick={submit} isLoading={isLoading} disabled={rulesLoading}>Complete task</Button>
    </div>
  );

  if (rulesLoading) {
    return (
      <Modal isOpen={open} onClose={onClose} title="Complete task" size="md" footer={footer}>
        <div className="space-y-2" data-testid="wa-completion-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 w-full animate-pulse rounded-lg bg-gray-100" />
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="Complete task" size="md" footer={footer}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">This task has Work Accountability requirements. Complete the fields below.</p>

        {unmet && (
          <div role="alert" data-testid="wa-completion-unmet" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="text-sm font-medium text-red-800 mb-1">Some requirements are not met:</div>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
              {unmet.map((u) => (
                <li key={u.requirement}>
                  <span className="font-medium">{UNMET_LABEL[u.requirement]}.</span> {u.explanation}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && <Banner kind="error" message={error} testId="wa-completion-error" />}

        {show.note && (
          <div>
            <label htmlFor="wa-cd-note" className="block text-sm font-medium text-gray-700 mb-1">
              Completion Note {rules.completionNote === 'required' && <span className="text-red-500">*</span>}
            </label>
            <textarea id="wa-cd-note" rows={3} value={completionNote} onChange={(e) => setCompletionNote(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        )}

        {show.finalTime && (
          <div>
            <label htmlFor="wa-cd-final" className="block text-sm font-medium text-gray-700 mb-1">Final Time Adjustment (minutes)</label>
            <input id="wa-cd-final" type="number" min={1} value={finalMinutes} onChange={(e) => setFinalMinutes(e.target.value)} placeholder="Optional — add any final time" className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        )}

        {show.variance && (
          <div>
            <label htmlFor="wa-cd-var" className="block text-sm font-medium text-gray-700 mb-1">
              Variance Explanation {rules.variance.explanationRequired && <span className="text-red-500">*</span>}
            </label>
            <textarea id="wa-cd-var" rows={2} value={varianceExplanation} onChange={(e) => setVarianceExplanation(e.target.value)} placeholder={`Explain if actual time exceeds the estimate by more than ${rules.variance.thresholdPct}%`} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        )}

        {show.proof && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proof of Done {rules.proofOfDone === 'required' && <span className="text-red-500">*</span>}
            </label>
            <TaskAttachments taskId={task.id} workspaceId={task.workspaceId} proofRequired={rules.proofOfDone === 'required'} />
          </div>
        )}
      </div>
    </Modal>
  );
}
